const safeString = (value) => (value ?? '').toString();

const mapTemplateItem = (item, mapValue) => {
  if (!item) return item;

  if (Array.isArray(item.items)) {
    return {
      ...item,
      items: item.items.map(child => mapTemplateItem(child, mapValue)),
    };
  }

  const value = safeString(item.snippet || item.token);
  if (!value) return item;

  // mapValue receives the item too, so composers can use its label
  // (e.g. products: "Roller Blind (ProductID <guid>)").
  const literal = mapValue(value, item);
  return {
    ...item,
    token: literal,
    snippet: literal,
  };
};

/**
 * Rewrites the token/snippet of every item in the named catalog group through
 * mapValue(value, item), leaving other groups untouched. Value-carrying groups
 * (fixed picklists, products) need a different literal form per editor —
 * OData vs JavaScript vs prompt text — so each editor maps its own copy.
 */
export const mapCatalogGroup = (catalog, groupName, mapValue) => (Array.isArray(catalog) ? catalog : []).map(group => {
  if (group?.group !== groupName) return group;

  return {
    ...group,
    items: Array.isArray(group.items)
      ? group.items.map(item => mapTemplateItem(item, mapValue))
      : [],
  };
});

/**
 * The catalog group that holds fixed-picklist codes (the server's workflow metadata
 * "FixedPicklists -> Entity -> Field -> Value" tree and the client's lookup-derived
 * "FixedPicklists -> Field -> Value" tree alike). The group NAME is the convention the
 * per-editor literal mappers key on; a group may carry a translated `label` for display.
 */
export const FIXED_PICKLISTS_TEMPLATE_GROUP = 'FixedPicklists';

export const mapFixedPicklists = (catalog, mapValue) => mapCatalogGroup(catalog, FIXED_PICKLISTS_TEMPLATE_GROUP, mapValue);

export const toODataStringLiteral = (value) => `'${safeString(value).replace(/'/g, "''")}'`;

/**
 * T-SQL string literal. Same single-quote escaping as OData, but kept as its own
 * export because the two dialects diverge elsewhere: SQL has no bare-guid literal
 * form, so product ProductID tokens (bare guids for OData) must ALSO map through
 * this for SQL editors.
 */
export const toSqlStringLiteral = (value) => `'${safeString(value).replace(/'/g, "''")}'`;

export const toJavascriptStringLiteral = (value) => JSON.stringify(safeString(value));
